import React, { useState } from 'react';
import { Button, Group, ActionIcon, Menu, Tooltip } from '@mantine/core';
import { IconPlus, IconMicrophone, IconKeyboard, IconAlertCircle } from '@tabler/icons-react';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';
import { UpgradeModal } from './UpgradeModal';

interface NewConversationButtonProps {
  onStartTextConversation: () => void;
  onStartVoiceConversation?: () => void;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  variant?: 'filled' | 'outline' | 'light';
  className?: string;
  buttonText?: string;
}

/**
 * Button to start new conversations with subscription tier enforcement
 */
export function NewConversationButton({
  onStartTextConversation,
  onStartVoiceConversation,
  size = 'md',
  fullWidth = false,
  variant = 'filled',
  className,
  buttonText = 'New Conversation'
}: NewConversationButtonProps) {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<'voice' | 'limit'>('limit');
  const [isLoading, setIsLoading] = useState(false);
  
  const { 
    canStartNewConversation,
    canUseVoice,
    subscriptionTier
  } = useSubscriptionFeatures();

  console.log('[NewConversationButton] Received Features:', { 
    canStartNewConversation, 
    canUseVoice, 
    subscriptionTier 
  });

  /**
   * Handles starting a new text conversation with tier enforcement
   */
  const handleStartTextConversation = async () => {
    console.log('[NewConversationButton] handleStartTextConversation - checking canStartNewConversation:', canStartNewConversation);
    // If they can't start a new conversation (free tier), show upgrade modal
    if (!canStartNewConversation) {
      console.log('[NewConversationButton] handleStartTextConversation - !canStartNewConversation is TRUE - showing modal');
      setUpgradeFeature('limit');
      setShowUpgradeModal(true);
      return;
    }

    try {
      setIsLoading(true);
      // Start the conversation - no need to check limits anymore
      onStartTextConversation();
    } catch (error) {
      console.error('Failed to start conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles starting a voice conversation with feature access check
   */
  const handleStartVoiceConversation = async () => {
    console.log('[NewConversationButton] handleStartVoiceConversation - checking canStartNewConversation:', canStartNewConversation);
    // If they can't start a new conversation (free tier limit reached), show upgrade modal
    if (!canStartNewConversation) {
      console.log('[NewConversationButton] handleStartVoiceConversation - !canStartNewConversation is TRUE - showing modal');
      setUpgradeFeature('limit');
      setShowUpgradeModal(true);
      return;
    }

    // If they can use voice (Voice Connect/Trial), proceed directly
    // If they are Text Companion, allow them to proceed, restriction happens inside chat
    // If they are Free and haven't hit limit, allow them to proceed, restriction happens inside chat

    try {
      setIsLoading(true);
      // Start the voice conversation - no need to check limits anymore
      if (onStartVoiceConversation) {
        onStartVoiceConversation();
      }
    } catch (error) {
      console.error('Failed to start voice conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // If both text and voice options are available, show a dropdown menu
  if (onStartVoiceConversation) {
    return (
      <>
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <Button
              leftSection={<IconPlus size={16} />}
              loading={isLoading}
              size={size}
              fullWidth={fullWidth}
              variant={variant}
              className={className}
            >
              {buttonText}
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item 
              leftSection={<IconKeyboard size={14} />} 
              onClick={handleStartTextConversation}
            >
              Text Conversation
            </Menu.Item>
            
            <Menu.Item 
              leftSection={canUseVoice ? <IconMicrophone size={14} /> : <IconAlertCircle size={14} color="orange" />}
              onClick={handleStartVoiceConversation}
              disabled={isLoading}
            >
              Voice Conversation {!canUseVoice && "(Voice Connect)"}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>

        <UpgradeModal
          opened={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature={upgradeFeature}
          currentTier={subscriptionTier}
        />
      </>
    );
  }

  // Simple button for text-only interface
  return (
    <>
      <Button
        leftSection={<IconPlus size={16} />}
        onClick={handleStartTextConversation}
        loading={isLoading}
        size={size}
        fullWidth={fullWidth}
        variant={variant}
        disabled={!canStartNewConversation && isLoading}
        className={className}
      >
        {buttonText}
      </Button>

      <UpgradeModal
        opened={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
        currentTier={subscriptionTier}
      />
    </>
  );
}